import {Formik} from 'formik';
import {Alert, Stack, Text, TextInput, Title} from '@mantine/core';
import {AiOutlineInfoCircle, AiOutlineLock} from 'react-icons/ai';
import {FormWithButtons} from '@flexinet/ui-components';

import {InfrastructureStagesField} from './InfrastructureStagesField';
import {RegisterInfrastructureFormValues} from '../types-infrastructure';

// FNP-1016: register infrastructure that ALREADY EXISTS.
//
// Design doc §9: in production there are 9 applications and only TWO were
// orchestrator-provisioned. The other seven — Billing / Stripe / Marketplace /
// Admin Console, Flexi Bot, Flexi Email, Flexi Domains — are live, load-bearing
// production systems this platform never built, carrying zero provisioning
// fields. An admin must be able to record what is really there so teardown and
// inventory work for them too.
//
// Design doc §5: an orchestrator-provisioned record has both INTENT and ACTUAL.
// A registered record has only ACTUAL — the admin fills in what exists. Same
// entity, same shape, one half populated.
//
// ⚠️ THIS FORM RECORDS REALITY. IT CREATES NOTHING. No repository, no pipeline,
// no stack, no bucket is made by submitting it. An admin who believes otherwise
// would be waiting for infrastructure that will never appear, so the page says
// so before the first field rather than in a footnote.
//
// ⚠️ AND IT MUST NOT ARM A TEARDOWN. A registered record is `protected`, which
// refuses deprovisioning. The form does not expose that switch — protection is
// derived by the resolver from `origin: REGISTERED`, so it cannot be forgotten
// and cannot be turned off here.

interface Props {
    handleSubmit: (values: RegisterInfrastructureFormValues) => void;
    handleCancel: () => void;
    // Pre-fill from the legacy `Application` fields when they exist. The
    // FNP-998 repair form wrote these; registration should not make an admin
    // retype what the record already knows.
    initialValues?: Partial<RegisterInfrastructureFormValues>;
}

export const RegisterInfrastructureForm = (
    {
        handleSubmit,
        handleCancel,
        initialValues,
    }: Props) => {

    const values: RegisterInfrastructureFormValues = {
        sourceRepositoryName: initialValues?.sourceRepositoryName ?? '',
        infraRepositoryName: initialValues?.infraRepositoryName ?? '',
        sourcePipelineId: initialValues?.sourcePipelineId ?? '',
        infraPipelineId: initialValues?.infraPipelineId ?? '',
        deploymentAccountGroupId: initialValues?.deploymentAccountGroupId ?? '',
        // ⚠️ Starts EMPTY, not pre-seeded with dev/test/prod rows. Pre-seeded
        // rows are how empty strings get into the array — the admin adds the
        // stages that exist.
        stages: initialValues?.stages ?? [],
    };

    return (
        <Formik
            initialValues={values}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {(props) => (
                <FormWithButtons
                    {...props}
                    handleCancel={handleCancel}
                >
                    <Stack gap={'lg'}>
                        <Alert
                            variant={'light'}
                            color={'blue'}
                            icon={<AiOutlineInfoCircle/>}
                            title={'This records existing infrastructure — it does not create any'}
                        >
                            <Text size={'sm'}>
                                Saving this form writes an inventory record describing
                                infrastructure that <strong>already exists</strong>. It does not
                                create, change, move or deploy any AWS resource. Nothing is
                                built as a result of submitting it.
                            </Text>
                            <Text
                                size={'sm'}
                                mt={'xs'}
                            >
                                Use it for applications this platform never provisioned, so that
                                inventory and decommissioning know what is really there.
                            </Text>
                        </Alert>

                        <Alert
                            variant={'light'}
                            color={'green'}
                            icon={<AiOutlineLock/>}
                            title={'A registered record is protected from teardown'}
                        >
                            <Text size={'sm'}>
                                Because nothing on this platform provisioned these resources,
                                the record is marked <strong>protected</strong> and
                                decommissioning will <strong>refuse</strong> to tear it down.
                                That protection is applied automatically and cannot be switched
                                off from this form.
                            </Text>
                        </Alert>

                        <div>
                            <Title
                                order={5}
                                mb={'xs'}
                            >
                                Repositories
                            </Title>
                            <Text
                                size={'sm'}
                                c={'dimmed'}
                                mb={'sm'}
                            >
                                Shared across every stage — a provisioned application holds two
                                repositories and two pipelines in total, not two per stage.
                                Leave a field blank if the resource does not exist.
                            </Text>
                            <Stack gap={'sm'}>
                                <TextInput
                                    label={'Source repository'}
                                    description={'CodeCommit repository holding the application source'}
                                    placeholder={'my-app'}
                                    name={'sourceRepositoryName'}
                                    value={props.values.sourceRepositoryName}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label={'Infrastructure repository'}
                                    description={'CodeCommit repository holding the CDK infrastructure'}
                                    placeholder={'my-app-infra'}
                                    name={'infraRepositoryName'}
                                    value={props.values.infraRepositoryName}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>

                        <div>
                            <Title
                                order={5}
                                mb={'xs'}
                            >
                                Pipelines
                            </Title>
                            <Stack gap={'sm'}>
                                <TextInput
                                    label={'Source pipeline id'}
                                    name={'sourcePipelineId'}
                                    value={props.values.sourcePipelineId}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label={'Infrastructure pipeline id'}
                                    name={'infraPipelineId'}
                                    value={props.values.infraPipelineId}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label={'Deployment account group id'}
                                    name={'deploymentAccountGroupId'}
                                    value={props.values.deploymentAccountGroupId}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>

                        <InfrastructureStagesField/>
                    </Stack>
                </FormWithButtons>
            )}
        </Formik>
    );
};
